/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { FSMFactoryModule } from "@vlinder-web/state-machine-factory-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { BaseApplication } from "./base/baseApplication";
import { UnitTest } from "./unit.test";
import {
    TrafficLightService,
    DeviceInfoService,
    DummyRestService,
    DummyGraphQLService,
    AuthService,
    ProfileService,
    OnboardOfficerService,
    ApplicationService,
    ProfileDetailsService,
    UserService,
    ReportService,
    ApplicationTableService,
    UpdatePaymentReceiptService,
    UpdateAssessmentService,
    ApproveApplicationService,
    GetApplicationDetailService,
    EditAccessStatusService,
    DoftStatementService,
    AdditionalInfoService
} from "../services";

import { DEFAULT_APP_CONFIG } from "../config/default.config";

export class VApplication extends BaseApplication {
    private static instance: VApplication;

    private constructor(options: any = {}) {
        super(options);

        this.setupBindings();

        //Modules
        this.initModules();

        //Services
        this.initServices();
    }

    static getInstance(options: any = {}) {
        if (!this.instance) {
            this.instance = new VApplication(options);
        }
        return this.instance;
    }

    // @ts-ignore
    async start() {
        await this.initRunLevel();
        await this.unitTest();
    }

    setupBindings() {
        //------------->>>>>--------------//
        this.configure("services.DummyRestService", {
            baseURL: DEFAULT_APP_CONFIG?.servers.rest.dummyRestServer.baseURL,
        });
        //------------->>>>>--------------//
    }

    async initRunLevel() {
        const storageSrv = StorageModule.getInstance();
        // const DEFAULT_APP_CONFIG = storageSrv.get("appConfig", "session");
        //------------->>>>>--------------//
        const graphqlModule = this.getSync(
            "services.GraphqlServerModule"
        ) as GraphqlServerModule;
        if (graphqlModule) {
            type TClient = typeof DEFAULT_APP_CONFIG.servers.graphql[0];
            DEFAULT_APP_CONFIG?.servers.graphql.forEach(
                async (client: TClient) => {
                    await graphqlModule
                        .createClient(client.name, {
                            url: client.url,
                            getContext: this.getGraphQLContext.bind(this),
                        })
                        .then((res) => {
                            console.log(`initRunLevel check`, res);
                        })
                        .catch((err) => {
                            console.warn(
                                `error in initRunLevel  ${err.message}`
                            );
                        });
                }
            );
        }
        //------------->>>>>--------------//
        const storageService = this.getSync(
            "services.StorageModule"
        ) as StorageModule;
        await storageService.init();
    }

    async unitTest() {
        const ut = new UnitTest(VApplication);
        ut.run();
    }

    initModules() {
        this.service("services.FSMFactoryModule", FSMFactoryModule);
        this.service("services.EventBusModule", EventBusModule);
        this.service("services.GraphqlServerModule", GraphqlServerModule);
        this.service("services.StorageModule", StorageModule);
    }

    initServices() {
        this.service("services.AuthService", AuthService);
        this.service("services.TrafficLightService", TrafficLightService);
        this.service("services.DummyRestService", DummyRestService);
        this.service("services.DeviceInfoService", DeviceInfoService);
        this.service("services.DummyGraphQLService", DummyGraphQLService);
        this.service("services.ProfileService", ProfileService);
        this.service("services.OnboardOfficerService", OnboardOfficerService);
        this.service("services.ApplicationService", ApplicationService);
        this.service("services.AdditionalInfoService", AdditionalInfoService);
        this.service(
            "services.ApplicationTableService",
            ApplicationTableService
        );
        this.service(
            "services.UpdatePaymentReceiptService",
            UpdatePaymentReceiptService
        );
        this.service(
            "services.UpdateAssessmentService",
            UpdateAssessmentService
        );
        this.service(
            "services.ApproveApplicationService",
            ApproveApplicationService
        );
        this.service(
            "services.GetApplicationDetailService",
            GetApplicationDetailService
        );
        this.service(
            "services.EditAccessStatusService",
            EditAccessStatusService
        );
        this.service("services.DoftStatementService", DoftStatementService);
        this.service("services.ReportService", ReportService);
    }

    private getGraphQLContext() {
        const loginService = this.getSync(
            "services.AuthService"
        ) as AuthService;
        const deviceInfoService = this.getSync(
            "services.DeviceInfoService"
        ) as DeviceInfoService;
        //FIXME:
        return {
            headers: {
                authentication: loginService?.getToken(),
                headers: {
                    ...{ ...deviceInfoService?.getDeviceInfo() },
                    ...{ ...deviceInfoService?.getAppInfo() },
                },
            },
        };
    }
}
